// TODO: Replace these placeholders with the real business details before going live.
export const BUSINESS = {
  name: "Lehi Trades SEO",
  shortName: "Lehi Trades SEO",
  tagline: "SEO and Google Ads built for trades contractors in Utah County.",
  phone: "(951) 490-1006",
  phoneHref: "tel:+19514901006",
  email: "chrisdickerman93@gmail.com",
  address: {
    street: "2600 W Executive Pkwy",
    locality: "Lehi",
    region: "UT",
    postalCode: "84043",
    country: "US",
  },
  hours: "Mon–Fri 8am–6pm MT",
  founded: 2019,
  license: "Utah Business #LTS-2019",
} as const;

export type ServiceSlug =
  | "local-seo"
  | "google-ads"
  | "gbp"
  | "web-design"
  | "generative-ai-marketing";

export interface Service {
  slug: ServiceSlug;
  name: string;
  short: string;
  tagline: string;
  description: string;
  jobs: string[];
  faqs: { q: string; a: string }[];
}

export const SERVICES: Service[] = [
  {
    slug: "local-seo",
    name: "Local SEO for Trades",
    short: "Local SEO",
    tagline: "Rank in the Google Map Pack for every city you serve.",
    description:
      "Local SEO campaigns built specifically for HVAC, plumbing, electrical, roofing, and remodeling contractors along the Wasatch Front. We optimize your site, citations, and content so you show up for 'near me' searches in every city you cover — not just your home base.",
    jobs: [
      "Local keyword research and city-page strategy",
      "On-page SEO and technical audits",
      "Map Pack and Google Business Profile optimization",
      "City-by-city landing page buildout",
      "Citation cleanup and local link building",
      "Monthly rank tracking and transparent reporting",
    ],
    faqs: [
      {
        q: "How long until I see results from local SEO?",
        a: "Most trades clients see Map Pack movement in 60–90 days and meaningful lead volume by month 4–6. We share a 90-day baseline so you can see the trajectory before a renewal decision.",
      },
      {
        q: "Do you work with contractors outside Lehi?",
        a: "Yes. We're based in Lehi and serve trades companies across Utah County and the greater Salt Lake metro — Provo, Orem, Saratoga Springs, American Fork, Eagle Mountain, and the south end of Salt Lake County.",
      },
      {
        q: "Will you build city pages for every town I service?",
        a: "That's the whole strategy. We build a unique, indexable page for each city you want to rank in, with real local content — no spun copy or duplicate templates.",
      },
    ],
  },
  {
    slug: "google-ads",
    name: "Google Ads for Trades (SEM/PPC)",
    short: "Google Ads",
    tagline: "Lead-gen campaigns built for emergency and service calls.",
    description:
      "We run Google Search, Local Service Ads (LSAs), and Performance Max campaigns for trades contractors. Every campaign is built around cost-per-lead, not vanity clicks — with call tracking, lead scoring, and weekly optimization so you know exactly what each booked job costs.",
    jobs: [
      "Google Search and Performance Max campaigns",
      "Local Service Ads (LSAs) setup and management",
      "Call tracking and lead scoring",
      "Negative keyword and budget management",
      "Landing page conversion optimization",
      "Weekly performance reviews and reporting",
    ],
    faqs: [
      {
        q: "What's a realistic cost per lead for HVAC or plumbing?",
        a: "Along the Wasatch Front, we typically see $35–$95 per qualified lead on Search and LSAs once a campaign is optimized — varies by trade, season, and service area.",
      },
      {
        q: "Do you require a long-term contract?",
        a: "No. Management is month-to-month after a 90-day initial commitment. The 90 days is just so we have time to actually optimize — pulling the plug at 30 days never produces real data.",
      },
      {
        q: "Can you set up Local Service Ads?",
        a: "Yes — we handle the Google Guaranteed verification, license uploads, and the daily lead disputes that keep your cost per lead honest.",
      },
    ],
  },
  {
    slug: "gbp",
    name: "Google Business Profile Management",
    short: "GBP",
    tagline: "Win the Map Pack with a profile that actually gets managed.",
    description:
      "Hands-on Google Business Profile management for trades contractors: weekly posts, photo uploads from real jobs, review-response, Q&A monitoring, and category and service tuning. The Map Pack is where homeowners are calling from — your profile has to earn it.",
    jobs: [
      "Profile audit and category optimization",
      "Weekly Google Posts and service updates",
      "Geo-tagged photo uploads from job sites",
      "Review generation workflow and response",
      "Q&A and messaging monitoring",
      "Suspension recovery and reinstatement",
    ],
    faqs: [
      {
        q: "Can you help if my profile was suspended?",
        a: "Yes. We handle GBP reinstatements regularly — including service-area business (SAB) suspensions, which are common in the trades.",
      },
      {
        q: "How do you get more reviews?",
        a: "We set up a text-and-email request flow that fires after each completed job, plus train your office staff on the right ask. Most clients double review velocity inside 60 days.",
      },
      {
        q: "Do you manage multiple location profiles?",
        a: "Yes — multi-location and franchise trades brands are part of our book. We use the GBP API for bulk updates so nothing gets stale.",
      },
    ],
  },
  {
    slug: "web-design",
    name: "Trades Website Design",
    short: "Web Design",
    tagline: "Fast, conversion-built websites for contractors.",
    description:
      "We design and build websites for trades companies that load fast, rank well, and turn visitors into booked jobs. Every site ships with the technical SEO baseline and the call-tracking, form, and chat integrations your campaigns need from day one.",
    jobs: [
      "Conversion-focused trades website design",
      "Core Web Vitals and page-speed optimization",
      "Service and city-page architecture",
      "Call tracking, form, and chat integration",
      "Schema markup and technical SEO baseline",
      "Easy-to-edit CMS handoff",
    ],
    faqs: [
      {
        q: "How long does a new contractor website take?",
        a: "Most builds ship in 4–6 weeks from kickoff. We start running SEO and ads on the existing site so you don't lose lead-flow during the rebuild.",
      },
      {
        q: "Do you do redesigns or only new sites?",
        a: "Both. For redesigns we do a full SEO migration plan — URL mapping, redirects, content parity — so rankings don't tank on launch.",
      },
      {
        q: "Do I own the website when we're done?",
        a: "Yes. Code, content, hosting accounts, and analytics all belong to you. No proprietary lock-in.",
      },
    ],
  },
  {
    slug: "generative-ai-marketing",
    name: "Generative AI Marketing for Trades",
    short: "Generative AI",
    tagline: "Get found in ChatGPT, Gemini, and Google's AI Overviews.",
    description:
      "Homeowners are starting their contractor search inside ChatGPT, Perplexity, Gemini, and Google's AI Overviews — not just the blue links. We build the structured content, schema, citations, and AI-readable answers that get your trades business named, linked, and recommended when someone asks an LLM 'who's the best plumber in Lehi?'",
    jobs: [
      "GEO (Generative Engine Optimization) audit and content strategy",
      "AI Overviews and ChatGPT citation engineering",
      "Schema markup tuned for LLM extraction (LocalBusiness, Service, FAQ, HowTo)",
      "AI-ready FAQ and Q&A content for every service and city page",
      "llms.txt and structured data feeds for crawlers like GPTBot, ClaudeBot, PerplexityBot",
      "AI-generated ad creative, landing pages, and review-response copy with human QA",
      "Monthly tracking of brand mentions across ChatGPT, Perplexity, and Gemini",
    ],
    faqs: [
      {
        q: "What is generative AI marketing for contractors?",
        a: "It's the practice of optimizing your website, schema, and citations so AI assistants like ChatGPT, Gemini, Perplexity, and Google's AI Overviews recommend your business by name when homeowners ask them for a contractor. It's the next layer on top of traditional SEO — and the contractors who set it up in 2026 will own the AI answer for years.",
      },
      {
        q: "Is GEO different from SEO?",
        a: "Yes. SEO targets the 10 blue links. GEO (Generative Engine Optimization) targets the cited sources inside an AI answer. The overlap is large — strong SEO helps GEO — but the ranking signals are different. LLMs reward clear answers, schema, freshness, and authoritative citations more than backlinks.",
      },
      {
        q: "Can you actually rank in ChatGPT?",
        a: "You can't rank in the classical sense, but you can absolutely become a cited or named source. ChatGPT, Perplexity, and Google AI Overviews all pull from indexed web content. We engineer the pages so they're the answer — short, structured, factual, and unambiguous.",
      },
      {
        q: "Do I need this if my regular SEO is working?",
        a: "If you're getting Map Pack and organic leads, you're in a good spot — but AI Overviews are eating into clickthrough for informational queries fast. Adding GEO now is a low-cost moat. The contractors who wait 18 months will be paying to catch up.",
      },
      {
        q: "How do you measure generative AI marketing results?",
        a: "We track three things monthly: brand mentions and citations across ChatGPT, Perplexity, and Gemini for your target queries; AI Overview presence in Google for your service + city searches; and the assisted-conversion lift in your analytics from AI-referred traffic.",
      },
    ],
  },
];

export interface CityArea {
  slug: string;
  name: string;
  region: string;
  blurb: string;
  neighborhoods: string[];
}

export const CITIES: CityArea[] = [
  {
    slug: "lehi-ut",
    name: "Lehi",
    region: "UT",
    blurb:
      "Our home base. We work with HVAC, plumbing, electrical, and roofing contractors across Lehi and the Silicon Slopes corridor.",
    neighborhoods: ["Traverse Mountain", "Thanksgiving Point", "Silicon Slopes", "Holbrook Farms", "Spring Creek"],
  },
  {
    slug: "american-fork-ut",
    name: "American Fork",
    region: "UT",
    blurb:
      "SEO and Google Ads for trades contractors competing across American Fork, Cedar Hills, and Highland.",
    neighborhoods: ["Downtown AF", "Cedar Hills", "Highland", "Alpine"],
  },
  {
    slug: "saratoga-springs-ut",
    name: "Saratoga Springs",
    region: "UT",
    blurb:
      "Fast-growing west side of Utah Lake — strong demand for HVAC, plumbing, and remodeling contractors who can rank for new neighborhoods.",
    neighborhoods: ["Harvest Hills", "Fox Hollow", "The Cove", "Wander"],
  },
  {
    slug: "pleasant-grove-ut",
    name: "Pleasant Grove",
    region: "UT",
    blurb:
      "Trades SEO across Pleasant Grove, Lindon, and Manila — mixed older housing stock and new construction.",
    neighborhoods: ["Grove Creek", "Manila", "Lindon", "Downtown PG"],
  },
  {
    slug: "eagle-mountain-ut",
    name: "Eagle Mountain",
    region: "UT",
    blurb:
      "One of Utah's fastest-growing cities. We help trades contractors rank in new subdivisions before the competition does.",
    neighborhoods: ["City Center", "Ranches", "SilverLake", "Overland"],
  },
  {
    slug: "orem-ut",
    name: "Orem",
    region: "UT",
    blurb:
      "Dense, competitive market — SEO and PPC for Orem-area HVAC, plumbing, and home-service contractors.",
    neighborhoods: ["Downtown Orem", "Sharon Park", "Geneva Heights", "Northridge"],
  },
  {
    slug: "provo-ut",
    name: "Provo",
    region: "UT",
    blurb:
      "Provo trades market with heavy student-rental and homeowner demand. Local SEO and review velocity win here.",
    neighborhoods: ["Downtown Provo", "Edgemont", "Grandview", "Riverbottoms"],
  },
];

export function findService(slug: string): Service | undefined {
  return SERVICES.find((s) => s.slug === slug);
}

export function findCity(slug: string): CityArea | undefined {
  return CITIES.find((c) => c.slug === slug);
}

export interface Industry {
  slug: string;
  trade: string;
  name: string;
  tagline: string;
  description: string;
  keyword: string;
  pains: string[];
  wins: string[];
  faqs: { q: string; a: string }[];
}

export const INDUSTRIES: Industry[] = [
  {
    slug: "plumbers",
    trade: "Plumbers",
    name: "SEO for Plumbers",
    keyword: "seo for plumbers",
    tagline: "Rank for emergency plumbing calls in every city you serve.",
    description:
      "We run SEO and Google Ads for plumbing contractors across Utah County and the Wasatch Front. Drain cleaning, water heaters, slab leaks, repipes, sewer — we know the searches homeowners actually type when a pipe bursts, and we build the pages, citations, and ad campaigns that get the phone ringing.",
    pains: [
      "Map Pack dominated by 2–3 national franchises",
      "Lead vendors selling the same call to five plumbers",
      "Google Business Profile suspended for service-area issues",
      "Website built by a cousin that won't rank for 'plumber near me'",
    ],
    wins: [
      "City-by-city service pages targeting 'emergency plumber {city}'",
      "Google Local Service Ads with verified Google Guaranteed badge",
      "Review velocity workflow that doubles GBP reviews in 60 days",
      "Call tracking so you know which keyword booked which job",
    ],
    faqs: [
      {
        q: "How much does plumber SEO cost?",
        a: "Most plumbing clients invest $1,500–$3,500/mo depending on how many cities they want to rank in. There's no setup fee and management is month-to-month after a 90-day commit.",
      },
      {
        q: "Can you help with Local Service Ads?",
        a: "Yes — we handle the Google Guaranteed verification, license uploads, weekly lead disputes, and bid management that keep LSAs profitable.",
      },
      {
        q: "How fast will I see more plumbing leads?",
        a: "Google Ads can produce booked jobs in week one. Organic SEO and Map Pack movement typically take 60–120 days for a plumbing company in a competitive Utah market.",
      },
    ],
  },
  {
    slug: "hvac-contractors",
    trade: "HVAC Contractors",
    name: "SEO for HVAC Companies",
    keyword: "seo for hvac companies",
    tagline: "Win furnace season and AC season with rankings that hold up.",
    description:
      "HVAC is the most competitive home-service vertical in Utah. We run SEO, Google Ads, and Local Service Ads for HVAC contractors who want to stop renting leads from Networx and HomeAdvisor and start owning the searches in their own service area.",
    pains: [
      "Seasonal demand spikes the competition is also bidding on",
      "ServiceTitan / national agencies pricing locals out",
      "Tune-up offers buried under franchise paid ads",
      "Reviews stuck at 4.6 stars and 47 reviews for two years",
    ],
    wins: [
      "Furnace + AC service pages for every city in your radius",
      "Performance Max + LSAs balanced against organic for lower CPL",
      "Tune-up landing pages tuned for seasonal conversion",
      "GBP weekly posts during heating + cooling spikes",
    ],
    faqs: [
      {
        q: "Can a local HVAC company outrank ServiceTitan-managed competitors?",
        a: "Yes — but only with deep city-page architecture and consistent content. ServiceTitan sites tend to be templated; a custom, city-specific buildout beats them on long-tail and Map Pack.",
      },
      {
        q: "Do you handle tune-up promo campaigns?",
        a: "Yes. We run seasonal tune-up offers across Search, LSAs, and email with dedicated landing pages so each promo is trackable to booked revenue.",
      },
      {
        q: "What's a realistic cost per HVAC lead?",
        a: "On the Wasatch Front we see $45–$120 per qualified HVAC lead on Search, lower on LSAs. Repair calls run cheaper than install leads.",
      },
    ],
  },
  {
    slug: "electricians",
    trade: "Electricians",
    name: "SEO for Electricians",
    keyword: "seo for electricians",
    tagline: "Panel upgrades, EV chargers, rewires — rank for the high-ticket jobs.",
    description:
      "Electrical contractors lose money on $150 service calls and make it back on $4,000 panel upgrades and EV charger installs. We build SEO and ad campaigns around the high-ticket keywords — and the Map Pack rankings that get homeowners to call you first.",
    pains: [
      "Too many low-ticket service calls, not enough installs",
      "EV charger demand exploding but nobody finds your site",
      "Generic 'electrician near me' searches going to chains",
      "Old website with no electrical service pages at all",
    ],
    wins: [
      "Service pages for panel upgrades, EV chargers, rewires, generators",
      "Geo pages for each Utah County city you serve",
      "Google Ads tuned for install intent, not just service calls",
      "GBP categories and service list dialed for high-ticket discovery",
    ],
    faqs: [
      {
        q: "Can you target EV charger install keywords?",
        a: "Yes — Tesla Wall Connector, NEMA 14-50, Level 2 charger install. These are growing 30%+ year over year in Utah County and most local electricians have zero content for them.",
      },
      {
        q: "Do you work with commercial electricians?",
        a: "Mostly residential and light commercial. Pure commercial / industrial electrical SEO is a different game and we'd refer you out.",
      },
      {
        q: "How long until panel-upgrade keywords rank?",
        a: "Long-tail electrical keywords like 'panel upgrade {city}' often rank within 60–90 days because most competitors haven't written for them.",
      },
    ],
  },
  {
    slug: "roofers",
    trade: "Roofers",
    name: "SEO for Roofing Contractors",
    keyword: "roofing seo",
    tagline: "Storm season, insurance jobs, full re-roofs — show up first.",
    description:
      "Roofing is a high-ticket, high-intent vertical where one ranked keyword can pay for a year of SEO. We build campaigns that capture storm-damage searches, full re-roof keywords, and the long-tail 'best roofer in {city}' queries that drive estimate requests.",
    pains: [
      "Storm-chaser companies flooding the area after every hail event",
      "Insurance claim keywords dominated by national lead vendors",
      "Map Pack reviews lower than competitors who pay for fakes",
      "Website looks like 2014 and Google knows it",
    ],
    wins: [
      "Service pages for asphalt, metal, tile, TPO, repair",
      "Storm-damage and insurance-claim content that ranks",
      "City pages for every Utah County town in your service area",
      "Authentic review velocity that outpaces storm chasers",
    ],
    faqs: [
      {
        q: "Do you do roofing SEO for storm restoration?",
        a: "Yes. We build storm-event landing pages, run paid campaigns the day a hail event hits, and keep evergreen content ranking between storms.",
      },
      {
        q: "Can you help with insurance-claim keywords?",
        a: "Yes — we build content around the actual insurance-claim process, which builds trust and ranks for the long-tail informational searches homeowners run before calling.",
      },
      {
        q: "Is roofing SEO worth it with how competitive it is?",
        a: "For one full re-roof at $18–30k, almost any SEO budget pays back inside 60 days. Roofing has the best LTV-to-CAC math of any trade we work with.",
      },
    ],
  },
];

export function findIndustry(slug: string): Industry | undefined {
  return INDUSTRIES.find((i) => i.slug === slug);
}

export interface BlogPost {
  slug: string;
  title: string;
  description: string;
  keyword: string;
  publishedAt: string;
  readMinutes: number;
  excerpt: string;
  body: { heading: string; paragraphs: string[]; bullets?: string[] }[];
}

export const BLOG_POSTS: BlogPost[] = [
  {
    slug: "youtube-reddit-quora-for-trades-seo",
    title: "Search Everywhere: YouTube, Reddit, and Quora for Trades SEO in 2026",
    description:
      "AI models pull from the whole web — not just your site. Here's how trades contractors build off-site authority on YouTube, Reddit, Quora, and podcasts.",
    keyword: "search everywhere trades seo",
    publishedAt: "2026-06-20",
    readMinutes: 8,
    excerpt:
      "AI Overviews and answer engines pull from the entire digital landscape, not just your blog. Trades contractors who show up on YouTube, Reddit, Quora, and podcasts are the ones getting named in AI answers.",
    body: [
      {
        heading: "Why off-site matters more in the AI era",
        paragraphs: [
          "ChatGPT, Perplexity, Gemini, and Google's AI Overviews don't just index your website. They synthesize answers from YouTube transcripts, Reddit threads, Quora answers, podcast episodes, and press mentions. A contractor with strong off-site presence gets named in AI answers even when their own site is mid-tier.",
        ],
      },
      {
        heading: "Repurpose your blog posts into short YouTube videos",
        paragraphs: [
          "Every long-form post on your site is the script for a 3–5 minute YouTube short. Talking-head, phone camera, no production budget — YouTube prefers consistency over polish. The bonus: YouTube videos appear directly inside Google SERPs for trades queries, often above blue-link results.",
        ],
      },
      {
        heading: "Answer questions on Reddit and Quora",
        paragraphs: [
          "r/HVAC, r/Plumbing, r/electricians, r/HomeImprovement, and r/marketing all have active threads where homeowners and contractors ask the exact questions your service pages answer. Spend 30 minutes a week giving real, helpful answers and link back only when it's genuinely the best resource.",
          "Quora's traffic is smaller, but its answers get cited by AI models with surprising frequency.",
        ],
      },
      {
        heading: "Digital PR and podcast guesting",
        paragraphs: [
          "One quote in a trade-association article or a guest appearance on a home-services podcast carries more E-E-A-T weight in 2026 than a dozen low-quality directory backlinks. Aim for two podcast guest spots and one industry mention per quarter.",
        ],
      },
      {
        heading: "Tie it back with sameAs schema",
        paragraphs: [
          "Every off-site profile you build (YouTube channel, Reddit account, LinkedIn, podcast guest page) should be listed in your Organization schema's sameAs property. That tells crawlers and LLMs that all these entities are the same business — and consolidates the authority.",
        ],
      },
      {
        heading: "Next steps",
        paragraphs: [
          "If you want help building an off-site distribution program for your trades business — YouTube, Reddit, podcasts, digital PR — request a free generative-AI marketing audit. We'll map where you already show up and where the easy wins are.",
        ],
      },
    ],
  },
  {
    slug: "local-seo-for-contractors-guide",
    title: "Local SEO for Contractors: The 2026 Playbook",
    description:
      "A step-by-step local SEO guide for contractors — Map Pack, city pages, GBP, reviews, and the technical baseline that actually moves rankings.",
    keyword: "local seo for contractors",
    publishedAt: "2026-06-01",
    readMinutes: 12,
    excerpt:
      "If you're a contractor trying to rank in Google's Map Pack across multiple cities, this is the exact playbook we run for our trades clients in Utah.",
    body: [
      {
        heading: "Why local SEO is different for contractors",
        paragraphs: [
          "Local SEO for contractors is not the same as local SEO for restaurants or retail. A homeowner searching 'plumber near me' or 'roof repair {city}' is in buying mode — usually within hours of needing the job done. The Map Pack is where those calls originate, and three slots is all that matters.",
          "The contractor's job is to earn one of those three slots in every city they serve. Not just the city the truck is parked in.",
        ],
      },
      {
        heading: "The five things that move Map Pack rankings",
        paragraphs: [
          "After running local SEO campaigns for HVAC, plumbing, electrical, and roofing companies across Utah County, we've narrowed the levers that actually matter:",
        ],
        bullets: [
          "Proximity to the searcher — you can't fake this, but you can build city-specific landing pages to extend your reach.",
          "Review count and velocity — fresh reviews matter more than total count. Stale 5-star profiles get outranked by active 4.6 profiles.",
          "Google Business Profile completeness — categories, services, photos, posts, Q&A. Every field filled is a signal.",
          "On-site relevance — a real, indexable page for each city and each service. Not one 'service areas' page with a list of towns.",
          "Citation consistency — name, address, phone matching across the 30–50 sites that actually feed Google's local graph.",
        ],
      },
      {
        heading: "City pages, done right",
        paragraphs: [
          "The biggest unlock for most contractors is building one unique, content-rich page per city in their service area. Not a templated 'we serve {city}' block with a swapped variable — Google's spam systems pattern-match on that and discount the whole site.",
          "A real city page has local proof: neighborhoods you've worked in, a recent job photo, a customer testimonial from that city if you have one, and a service list specific to that city's housing stock (older homes need repipes, new builds need warranty work).",
        ],
      },
      {
        heading: "GBP weekly cadence",
        paragraphs: [
          "Google Business Profile is a living asset. The profiles that win the Map Pack post weekly, upload geo-tagged photos from real jobs, and respond to every review within 48 hours.",
          "The contractors we manage average four Google Posts a month and 8–12 review responses. That cadence alone moves rankings.",
        ],
      },
      {
        heading: "Review velocity beats review count",
        paragraphs: [
          "Google's local algorithm weights recent reviews more than old ones. A contractor with 47 reviews and one new review a week will often outrank a contractor with 240 reviews and one new review a quarter.",
          "The fix: a text-and-email review request that fires automatically after each completed job, plus 60 seconds of office-staff training to ask out loud.",
        ],
      },
      {
        heading: "The technical baseline most contractor sites are missing",
        paragraphs: [
          "Before any of the above pays off, the site itself has to clear a technical bar:",
        ],
        bullets: [
          "Mobile page speed (LCP under 2.5s) — most contractor sites are 4–6s.",
          "Schema markup for LocalBusiness, Service, and FAQPage.",
          "A canonical tag, sitemap.xml, and robots.txt that aren't blocking the site.",
          "HTTPS everywhere, no mixed content, no 404s in the main nav.",
        ],
      },
      {
        heading: "How long this takes",
        paragraphs: [
          "Realistic timeline for a contractor starting from a basic site and a half-finished GBP: Map Pack movement at 60–90 days, meaningful lead volume by month 4–6, full ROI inside the first year.",
          "Anyone promising faster is selling something. Anyone telling you SEO doesn't work for contractors is selling lead vendors.",
        ],
      },
      {
        heading: "Next steps",
        paragraphs: [
          "If you want us to run this playbook for your trades business in Utah County, we offer a free local SEO audit — we'll show you exactly where your rankings, GBP, and site stand against the top 3 competitors in each of your cities. No pitch deck, no contract pressure.",
        ],
      },
    ],
  },
  {
    slug: "generative-ai-marketing-for-contractors",
    title: "Generative AI Marketing for Contractors: The 2026 GEO Playbook",
    description:
      "How trades contractors get cited inside ChatGPT, Perplexity, Gemini, and Google AI Overviews — the schema, content, and citation strategy that wins.",
    keyword: "generative ai marketing",
    publishedAt: "2026-06-03",
    readMinutes: 10,
    excerpt:
      "ChatGPT, Perplexity, and Google's AI Overviews are quietly replacing the second-page Google result. Here's how trades contractors get named — and recommended — inside the answer.",
    body: [
      {
        heading: "The shift: from blue links to AI answers",
        paragraphs: [
          "Five years ago, a homeowner with a leaking water heater searched Google, scanned three results, and called whoever picked up. Today, more of them open ChatGPT and ask 'who's the best plumber in Lehi, Utah for a water heater install?' — and they call whoever ChatGPT names first.",
          "This is Generative Engine Optimization (GEO). It runs on top of traditional SEO and decides which contractors show up inside AI answers, not just inside the 10 blue links.",
        ],
      },
      {
        heading: "Where AI assistants pull contractor data from",
        paragraphs: [
          "ChatGPT, Perplexity, Gemini, and Google's AI Overviews don't share one index, but they pull from overlapping sources:",
        ],
        bullets: [
          "Your website's structured content and schema markup",
          "Google Business Profile data and reviews",
          "High-authority directories (Yelp, Angi, BBB, BuildZoom, HomeAdvisor)",
          "News articles, press mentions, and local blog coverage",
          "Reddit threads, forum answers, and Q&A sites for trust signals",
        ],
      },
      {
        heading: "What LLMs actually reward",
        paragraphs: [
          "After auditing dozens of contractor sites against AI-assistant citations, a clear pattern shows up. LLMs reward:",
        ],
        bullets: [
          "Direct, factual answers placed in the first 100 words of a page",
          "FAQ schema on every service and city page — LLMs love extractable Q&A",
          "Clear entity signals: business name, address, phone, license, founded date, areas served",
          "Freshness — pages updated within the last 6 months get cited more",
          "Internal linking that builds topical depth, not just breadth",
        ],
      },
      {
        heading: "Schema markup is the unlock most contractors are missing",
        paragraphs: [
          "LLMs extract structured data far more reliably than they parse prose. Every contractor site should ship with LocalBusiness, Service, FAQPage, and Review schema at minimum. If you run multiple locations, Organization + Place + sameAs links to your social profiles tie it all together.",
          "We see a measurable lift in AI Overview presence within 30–45 days of deploying complete schema across a contractor site.",
        ],
      },
      {
        heading: "llms.txt — the new robots.txt for AI crawlers",
        paragraphs: [
          "A growing convention is shipping an /llms.txt file that gives AI crawlers a clean, summarized map of your site. It's not yet a strict standard, but GPTBot, ClaudeBot, and PerplexityBot increasingly look for it.",
          "Pair it with explicit allow/disallow rules in robots.txt for each AI user-agent so you control which pages get used as training and citation sources.",
        ],
      },
      {
        heading: "Content patterns that get cited",
        paragraphs: [
          "If you want ChatGPT to name your business, write the way ChatGPT answers:",
        ],
        bullets: [
          "Lead with the direct answer, then the explanation",
          "Use short, declarative sentences with numbers (cost, timeline, %)",
          "Add a 'How we differ' or 'Why choose us' section with concrete differentiators",
          "Include a comparison table — LLMs extract them and reuse them in answers",
          "Cite your sources (utility websites, local code references) so the LLM sees your page as authoritative",
        ],
      },
      {
        heading: "Brand-mention monitoring",
        paragraphs: [
          "You can't optimize what you can't measure. We track monthly how often our trades clients are named inside ChatGPT, Perplexity, and Gemini for a defined set of target queries — 'best plumber in {city}', 'HVAC repair {city}', 'electrician near me in {city}'.",
          "The goal isn't to appear in every answer. It's to be one of the 2–3 names that come up consistently for your service area. That's the AI-era equivalent of owning the Map Pack.",
        ],
      },
      {
        heading: "What to do this quarter",
        paragraphs: [
          "If you're a contractor and you haven't touched GEO yet, here's the 90-day starter:",
        ],
        bullets: [
          "Deploy LocalBusiness, Service, and FAQPage schema across every page",
          "Add an llms.txt and update robots.txt for AI crawlers",
          "Rewrite the top 5 service pages with direct answers in the first 100 words",
          "Add 6–10 FAQs per service and per city page",
          "Publish 2 pillar pieces covering your most-asked customer questions",
          "Start monthly brand-mention tracking across ChatGPT, Perplexity, Gemini",
        ],
      },
      {
        heading: "Next steps",
        paragraphs: [
          "If you want us to run the full GEO playbook for your trades business — schema, content, citations, monitoring — request a free generative AI marketing audit. We'll show you exactly where you stand in ChatGPT, Perplexity, and Google AI Overviews today, and what it takes to own the answer in your service area.",
        ],
      },
    ],
  },
];

export function findBlogPost(slug: string): BlogPost | undefined {
  return BLOG_POSTS.find((p) => p.slug === slug);
}

// ---------- Social profiles (off-site / Search Everywhere) ----------
// Fill in real URLs as channels go live. Empty strings are filtered everywhere.
export const SOCIAL_PROFILES: Record<
  "youtube" | "reddit" | "linkedin" | "x" | "quora" | "podcast",
  string
> = {
  youtube: "",
  reddit: "",
  linkedin: "",
  x: "",
  quora: "",
  podcast: "",
};

export function activeSocialUrls(): string[] {
  return Object.values(SOCIAL_PROFILES).filter((v) => v.length > 0);
}

// ---------- Topical authority hubs ----------
export interface HubSubTopic {
  title: string;
  blurb: string;
  href: string; // internal absolute path
}

export interface TradeHub {
  slug: "plumber" | "electrician" | "hvac" | "roofer";
  trade: string;
  title: string;
  tagline: string;
  answer: { question: string; answer: string; bullets?: string[] };
  intro: string;
  subTopics: HubSubTopic[];
}

export const TRADE_HUBS: TradeHub[] = [
  {
    slug: "plumber",
    trade: "Plumbers",
    title: "Plumber Marketing Hub",
    tagline: "Everything a plumbing contractor needs to dominate local search.",
    answer: {
      question: "How do plumbers get more leads from Google?",
      answer:
        "Plumbers win Google by stacking three things: a city-by-city service page architecture, an actively managed Google Business Profile with weekly review velocity, and Local Service Ads with the Google Guaranteed badge — in that order.",
      bullets: [
        "City pages: one indexable page per town you serve",
        "GBP: weekly posts, geo-tagged job photos, 48-hour review responses",
        "LSAs: Google Guaranteed badge + daily lead disputes",
      ],
    },
    intro:
      "Plumbing is the most search-driven trade in the home-service space. Homeowners search the moment a pipe bursts, and they call whoever shows up in the top three Map Pack slots. Below is every silo we build inside a plumbing marketing program.",
    subTopics: [
      { title: "Local SEO for Plumbers", blurb: "Map Pack rankings in every city you serve.", href: "/industries/plumbers" },
      { title: "Plumber Local SEO Playbook", blurb: "Step-by-step playbook for ranking 'plumber near me' across multiple cities.", href: "/blog/local-seo-for-contractors-guide" },
      { title: "Google Ads + LSAs for Plumbing", blurb: "Search + Local Service Ads tuned for emergency call intent.", href: "/services/google-ads" },
      { title: "GBP Management for Plumbers", blurb: "Weekly posts, review velocity, and suspension recovery.", href: "/services/gbp" },
      { title: "Generative AI / GEO for Plumbers", blurb: "Get cited inside ChatGPT, Gemini, and AI Overviews.", href: "/services/generative-ai-marketing" },
    ],
  },
  {
    slug: "electrician",
    trade: "Electricians",
    title: "Electrician Marketing Hub",
    tagline: "Rank for panel upgrades, EV chargers, and rewires — not $150 service calls.",
    answer: {
      question: "How do electricians get high-ticket install jobs from Google?",
      answer:
        "Electricians win the high-ticket jobs by building dedicated service pages for panel upgrades, EV chargers, rewires, and generators — then running Google Ads tuned for install intent, not generic service-call keywords.",
      bullets: [
        "One page per high-ticket service (panel, EV charger, rewire, generator)",
        "Geo pages for each city in your radius",
        "Search campaigns segmented by install vs. repair intent",
      ],
    },
    intro:
      "EV chargers and panel upgrades are growing 30%+ year over year in Utah County, and most local electricians have zero content for them. The electricians who build the pages now own the keyword for a decade.",
    subTopics: [
      { title: "Local SEO for Electricians", blurb: "Rank for panel upgrade, EV charger, and rewire keywords.", href: "/industries/electricians" },
      { title: "City-Page Strategy for Trades", blurb: "How to build one indexable page per city without duplicate content.", href: "/blog/local-seo-for-contractors-guide" },
      { title: "Google Ads for Electrical Installs", blurb: "Install-intent campaigns, not low-margin service calls.", href: "/services/google-ads" },
      { title: "GBP for Electricians", blurb: "Categories and service list dialed for high-ticket discovery.", href: "/services/gbp" },
      { title: "Generative AI / GEO for Electricians", blurb: "Get named in ChatGPT for 'best electrician in {city}'.", href: "/services/generative-ai-marketing" },
    ],
  },
  {
    slug: "hvac",
    trade: "HVAC Contractors",
    title: "HVAC Marketing Hub",
    tagline: "Furnace season, AC season, tune-up promos — own all three.",
    answer: {
      question: "What's the best marketing strategy for an HVAC company?",
      answer:
        "HVAC marketing wins on three seasonal cycles: furnace season (Sep–Feb), AC season (May–Aug), and tune-up promos in the shoulder months. The best programs balance organic city pages with Search, LSAs, and Performance Max to keep cost-per-lead under $120 year-round.",
      bullets: [
        "City pages for furnace + AC service in every city you cover",
        "Performance Max + LSAs balanced against organic for lower CPL",
        "Tune-up landing pages timed to spring and fall promos",
      ],
    },
    intro:
      "HVAC is the most competitive home-service vertical in Utah. National franchises and ServiceTitan-managed competitors are bidding against you on every keyword. The way to beat them is depth — more city pages, more seasonal content, more GBP activity.",
    subTopics: [
      { title: "Local SEO for HVAC Companies", blurb: "Rank for furnace and AC service in every city.", href: "/industries/hvac-contractors" },
      { title: "Seasonal HVAC Marketing", blurb: "How to time furnace, AC, and tune-up campaigns through the year.", href: "/blog/local-seo-for-contractors-guide" },
      { title: "Google Ads + LSAs for HVAC", blurb: "Search + Performance Max + LSAs tuned by season.", href: "/services/google-ads" },
      { title: "GBP for HVAC", blurb: "Weekly posts during heating and cooling spikes.", href: "/services/gbp" },
      { title: "Generative AI / GEO for HVAC", blurb: "Citations inside AI Overviews and ChatGPT.", href: "/services/generative-ai-marketing" },
    ],
  },
  {
    slug: "roofer",
    trade: "Roofing Contractors",
    title: "Roofer Marketing Hub",
    tagline: "Storm events, insurance claims, full re-roofs — show up first.",
    answer: {
      question: "How do roofers get more re-roof and insurance jobs from Google?",
      answer:
        "Roofers win Google by capturing storm-damage queries the day a hail event hits and ranking organically for 'best roofer in {city}' year-round. One ranked re-roof keyword often pays for a year of SEO.",
      bullets: [
        "Storm-event landing pages ready before the hail hits",
        "Insurance-claim content for the long-tail informational searches",
        "Authentic review velocity that outpaces storm chasers",
      ],
    },
    intro:
      "Roofing has the best LTV-to-CAC math of any trade. A single re-roof at $18–30k pays back almost any SEO budget inside 60 days. The trade-off is competitive density — storm chasers flood every event.",
    subTopics: [
      { title: "Local SEO for Roofers", blurb: "Rank for re-roof, storm damage, and insurance-claim keywords.", href: "/industries/roofers" },
      { title: "Local SEO Playbook", blurb: "Map Pack, city pages, and review velocity for trades.", href: "/blog/local-seo-for-contractors-guide" },
      { title: "Google Ads for Roofers", blurb: "Storm-event campaigns and insurance-claim landing pages.", href: "/services/google-ads" },
      { title: "GBP for Roofers", blurb: "Authentic review velocity to outpace storm chasers.", href: "/services/gbp" },
      { title: "Generative AI / GEO for Roofers", blurb: "Get named in AI answers for 'best roofer near me'.", href: "/services/generative-ai-marketing" },
    ],
  },
];

export function findTradeHub(slug: string): TradeHub | undefined {
  return TRADE_HUBS.find((h) => h.slug === slug);
}

// ---------- Case studies (E-E-A-T) ----------
export interface CaseStudyResult {
  metric: string;
  before: string;
  after: string;
  detail: string;
}

export interface CaseStudy {
  slug: string;
  client: string;        // anonymized OK
  trade: string;
  city: string;
  title: string;
  summary: string;       // for index + AnswerBlock
  challenge: string;
  approach: string[];
  timeline: string;
  results: CaseStudyResult[];
  pullQuote?: { quote: string; attribution: string };
  videoUrl?: string;     // YouTube embed URL (empty = no video)
  publishedAt: string;
  isExample: true;       // remove or flip to false once user replaces with real data
}

export const CASE_STUDIES: CaseStudy[] = [
  {
    slug: "example-roofer-2x-traffic",
    client: "Regional roofing contractor (anonymized)",
    trade: "Roofing",
    city: "Saratoga Springs, UT",
    title: "How we 2× organic traffic for a Utah County roofer in 6 months",
    summary:
      "Doubled organic sessions and tripled qualified estimate requests for a residential roofer competing against storm-chasers across the west side of Utah Lake.",
    challenge:
      "Storm-chaser companies flooded the area after every hail event, sweeping up insurance-claim keywords. The client's existing site had three pages and a 4-year-old GBP with stale photos.",
    approach: [
      "Built service pages for asphalt, metal, tile, TPO, and repair",
      "Shipped 9 city pages across Utah County with unique local content",
      "Set up a weekly GBP cadence: posts, geo-tagged job photos, review responses",
      "Deployed FAQ + Service schema across every page for AI/answer-engine pickup",
      "Launched a storm-event landing page template ready to activate within 24 hours",
    ],
    timeline: "6 months",
    results: [
      { metric: "Organic sessions", before: "1,420 / mo", after: "2,910 / mo", detail: "+105% lift, weighted toward city-page traffic." },
      { metric: "Qualified estimate requests", before: "11 / mo", after: "34 / mo", detail: "3.1× lift; tracked via form + call tracking." },
      { metric: "Map Pack presence", before: "2 cities", after: "7 cities", detail: "Top-3 Map Pack across the new city-page set." },
      { metric: "Avg. cost per estimate", before: "—", after: "$48", detail: "Organic-only; LSAs ran separately." },
    ],
    pullQuote: {
      quote: "We stopped buying leads from third parties inside the first quarter. The phone just rings now.",
      attribution: "Owner, regional roofing contractor",
    },
    videoUrl: "",
    publishedAt: "2026-05-10",
    isExample: true,
  },
  {
    slug: "example-plumber-cpl-cut",
    client: "Local plumbing company (anonymized)",
    trade: "Plumbing",
    city: "Lehi, UT",
    title: "Cutting cost-per-lead from $85 to $28 for a Lehi plumber",
    summary:
      "Restructured a bloated Google Ads account and added LSAs to drop cost-per-lead 67% while tripling booked job volume on the same monthly budget.",
    challenge:
      "The client had been running broad-match Search campaigns with no negative keyword discipline and no call tracking. CPL had drifted from $40 at launch to $85 over 14 months.",
    approach: [
      "Rebuilt campaigns around exact + phrase match for emergency call intent",
      "Layered a Local Service Ads campaign with Google Guaranteed verification",
      "Set up CallRail tracking so every booked job mapped to keyword + campaign",
      "Wrote conversion-built landing pages for water heater, drain, and slab leak",
      "Started a weekly negative-keyword sweep based on the search-terms report",
    ],
    timeline: "90 days",
    results: [
      { metric: "Cost per qualified lead", before: "$85", after: "$28", detail: "67% reduction across Search + LSAs blended." },
      { metric: "Booked jobs / mo", before: "22", after: "71", detail: "3.2× lift on the same media budget." },
      { metric: "Wasted spend (irrelevant queries)", before: "~38%", after: "~6%", detail: "After 90 days of negative-keyword work." },
    ],
    pullQuote: {
      quote: "I knew the old account was bleeding money. I didn't realize we could 3× volume without raising the budget.",
      attribution: "Owner, Lehi-area plumbing company",
    },
    videoUrl: "",
    publishedAt: "2026-04-22",
    isExample: true,
  },
  {
    slug: "example-hvac-map-pack",
    client: "HVAC contractor (anonymized)",
    trade: "HVAC",
    city: "Orem, UT",
    title: "From page 3 to top-3 Map Pack: HVAC contractor in 89 days",
    summary:
      "Moved a Utah County HVAC company from invisibility to top-3 Map Pack rankings across 5 cities, lifting click-to-call volume 140% in under a quarter.",
    challenge:
      "Client was being outranked by two ServiceTitan-managed national franchises. GBP was complete but inactive. Site had no city pages and no FAQ content.",
    approach: [
      "Audited and rewrote GBP categories, services, and business description",
      "Shipped 6 city pages with unique local content and FAQ schema",
      "Set a weekly GBP cadence: posts, photos, review responses within 48 hours",
      "Built a furnace-tune-up landing page for the shoulder-season promo",
      "Deployed LocalBusiness + Service + FAQPage + HVACBusiness schema graph",
    ],
    timeline: "89 days",
    results: [
      { metric: "Map Pack top-3 cities", before: "0", after: "5", detail: "Across Orem, Provo, Lehi, AF, PG." },
      { metric: "Click-to-call volume", before: "31 / mo", after: "74 / mo", detail: "+140% lift in 89 days." },
      { metric: "GBP review velocity", before: "1 / quarter", after: "8 / month", detail: "After staff training + automated request flow." },
    ],
    videoUrl: "",
    publishedAt: "2026-03-18",
    isExample: true,
  },
];

export function findCaseStudy(slug: string): CaseStudy | undefined {
  return CASE_STUDIES.find((c) => c.slug === slug);
}

// ---------- Programmatic SEO: city + trade local context ----------
// Real, public-record numbers + qualitative hooks per city. Used to render
// hyper-specific Trade + City landing pages with unique value (population,
// housing-age mix, weather/seasonal hooks) so they pass spam filters.
export interface CityLocalData {
  population: number;          // approx, latest census/estimate
  medianHomeAgeYears: number;  // older = more repipes, rewires, re-roofs
  growthRate: string;          // "fast", "steady", "mature"
  weatherHook: string;         // climate fact that drives trade demand
  housingNote: string;         // mix of housing stock
}

export const CITY_LOCAL_DATA: Record<string, CityLocalData> = {
  "lehi-ut": {
    population: 92000,
    medianHomeAgeYears: 18,
    growthRate: "fast",
    weatherHook: "Cold-snap mornings under 10°F drive a furnace-failure spike every January.",
    housingNote: "Mix of 2010-era Traverse Mountain builds and brand-new Holbrook construction.",
  },
  "american-fork-ut": {
    population: 36000,
    medianHomeAgeYears: 32,
    growthRate: "steady",
    weatherHook: "Older mainline plumbing in downtown AF is hit hard by deep-freeze events.",
    housingNote: "Established downtown stock plus newer Cedar Hills hillside builds.",
  },
  "saratoga-springs-ut": {
    population: 53000,
    medianHomeAgeYears: 11,
    growthRate: "fast",
    weatherHook: "Lake-effect wind events drive shingle-lift and gutter calls every spring.",
    housingNote: "Almost entirely post-2010 construction — warranty work and EV-charger installs dominate.",
  },
  "pleasant-grove-ut": {
    population: 40000,
    medianHomeAgeYears: 38,
    growthRate: "steady",
    weatherHook: "Aging cast-iron drain stacks in Manila and Grove Creek generate repipe leads year-round.",
    housingNote: "Mature housing stock with active remodel and re-roof demand.",
  },
  "eagle-mountain-ut": {
    population: 60000,
    medianHomeAgeYears: 9,
    growthRate: "fast",
    weatherHook: "Exposed western plateau — hail and wind events trigger storm-roof demand twice a year.",
    housingNote: "Mostly new-construction subdivisions; warranty work and finish-out are the volume.",
  },
  "orem-ut": {
    population: 98000,
    medianHomeAgeYears: 44,
    growthRate: "mature",
    weatherHook: "Dense older stock means furnace replacements peak in November and February.",
    housingNote: "Bungalows, mid-century ranches, and 80s tract — heavy demand for water heater and panel work.",
  },
  "provo-ut": {
    population: 116000,
    medianHomeAgeYears: 48,
    growthRate: "mature",
    weatherHook: "Older housing + student-rental turnover creates constant plumbing and electrical service calls.",
    housingNote: "Mix of historic homes, student rentals, and Edgemont/Riverbottoms estate properties.",
  },
};

export interface TradeLocalAngle {
  slug: TradeHub["slug"];
  trade: string;            // singular noun
  service: string;          // headline service word
  emergencyExample: string; // common emergency call
  highTicketExample: string; // common high-ticket job
  searchTerm: string;       // canonical "near me" term
}

export const TRADE_LOCAL_ANGLES: TradeLocalAngle[] = [
  {
    slug: "plumber",
    trade: "plumber",
    service: "plumbing",
    emergencyExample: "burst pipe at 2am",
    highTicketExample: "whole-home repipe or water-heater replacement",
    searchTerm: "plumber near me",
  },
  {
    slug: "electrician",
    trade: "electrician",
    service: "electrical",
    emergencyExample: "dead panel after a wind event",
    highTicketExample: "panel upgrade or Level 2 EV charger install",
    searchTerm: "electrician near me",
  },
  {
    slug: "hvac",
    trade: "HVAC contractor",
    service: "HVAC",
    emergencyExample: "no-heat call below freezing",
    highTicketExample: "full furnace + AC system replacement",
    searchTerm: "HVAC near me",
  },
  {
    slug: "roofer",
    trade: "roofer",
    service: "roofing",
    emergencyExample: "active leak after a wind or hail event",
    highTicketExample: "full asphalt or metal re-roof on an insurance claim",
    searchTerm: "roofer near me",
  },
];

export function findTradeAngle(slug: string): TradeLocalAngle | undefined {
  return TRADE_LOCAL_ANGLES.find((t) => t.slug === slug);
}

/** Every (trade × city) pair we generate a pSEO landing page for. */
export function listProgrammaticPages(): Array<{ trade: TradeLocalAngle; city: CityArea }> {
  const out: Array<{ trade: TradeLocalAngle; city: CityArea }> = [];
  for (const trade of TRADE_LOCAL_ANGLES) {
    for (const city of CITIES) out.push({ trade, city });
  }
  return out;
}

// ---------- Digital PR: press, podcasts, columns ----------
export interface PressFeature {
  kind: "podcast" | "column" | "feature";
  title: string;       // episode / article title
  outlet: string;      // podcast or publication name
  host?: string;       // for podcasts
  url: string;         // empty until live — filtered out when blank
  publishedAt: string; // YYYY-MM-DD
  blurb: string;
}

/**
 * Seed your real podcast appearances and guest columns here. Items with
 * empty URLs are filtered out everywhere — safe to ship pre-populated.
 */
export const PRESS_FEATURES: PressFeature[] = [
  // EXAMPLE — replace with real entries:
  // {
  //   kind: "podcast",
  //   title: "Owning the Map Pack in 2026",
  //   outlet: "The Roofing Podcast",
  //   host: "Heidi Ellsworth",
  //   url: "https://example.com/episode-123",
  //   publishedAt: "2026-06-12",
  //   blurb: "Chris breaks down city-page architecture for storm-restoration roofers.",
  // },
];

export function activePress(): PressFeature[] {
  return PRESS_FEATURES.filter((p) => p.url.length > 0);
}